import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { Bell, Clock, BellRinging, Warning, CalendarBlank, ChatCircleDots } from "@phosphor-icons/react";
import { toast } from "sonner";
import { DropdownMenu, DropdownMenuContent, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";

const STATUS_META = {
  ueberfaellig: { label: "Überfällig", cls: "bg-[#D32F2F] text-white" },
  heute: { label: "Heute", cls: "bg-[#F59E0B] text-white" },
  vorfrist: { label: "Vorfrist", cls: "bg-[#0EA5E9] text-white" },
  neu: { label: "Neu", cls: "bg-[#1A2238] text-white" },
};

const fmtDate = (s) => (s ? new Date(s + (s.length === 10 ? "T00:00:00" : "")).toLocaleDateString("de-DE", { day: "2-digit", month: "2-digit" }) : "");

export default function NotificationsBell() {
  const nav = useNavigate();
  const [data, setData] = useState({ items: [], counts: { gesamt: 0, ueberfaellig: 0, heute: 0, vorfrist: 0 } });
  const toasted = useRef(false);

  const load = async () => {
    try {
      const r = await api.get("/fristen/uebersicht");
      setData(r.data);
      // Beim ersten Laden: sofort informieren, wenn etwas brennt
      if (!toasted.current && (r.data.counts.ueberfaellig > 0 || r.data.counts.heute > 0)) {
        toasted.current = true;
        const c = r.data.counts;
        toast.warning(`${c.ueberfaellig} überfällig · ${c.heute} heute fällig`, {
          description: r.data.items.slice(0, 3).map(i => `• ${i.titel}`).join("\n"),
          duration: 9000,
          action: { label: "Fristen öffnen", onClick: () => nav("/app/fristen") },
        });
      }
    } catch { /* still */ }
  };

  useEffect(() => {
    load();
    const t = setInterval(load, 60000);
    return () => clearInterval(t);
    // eslint-disable-next-line
  }, []);

  const c = data.counts;
  const badgeCls = c.ueberfaellig > 0 ? "bg-[#D32F2F]" : c.heute > 0 ? "bg-[#F59E0B]" : "bg-[#0EA5E9]";

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button
          className="relative h-10 w-10 rounded-full hover:bg-[#F3F4F6] flex items-center justify-center text-[#4B5563]"
          data-testid="topbar-notifications"
          title="Fristen & Wiedervorlagen"
        >
          <Bell size={20} weight="duotone" />
          {c.gesamt > 0 && (
            <span className={`absolute -top-0.5 -right-0.5 min-w-[18px] h-[18px] px-1 rounded-full text-[10px] font-bold text-white flex items-center justify-center ${badgeCls}`} data-testid="notifications-badge">
              {c.gesamt > 99 ? "99+" : c.gesamt}
            </span>
          )}
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-[360px] p-0" data-testid="notifications-dropdown">
        <div className="px-4 py-3 border-b border-[#E5E7EB] flex items-center justify-between">
          <div>
            <p className="text-sm font-semibold text-[#0A192F]">Fristen & Wiedervorlagen</p>
            <p className="text-[11px] text-[#6B7280]">{c.ueberfaellig} überfällig · {c.heute} heute · {c.vorfrist} Vorfristen{c.portal ? ` · ${c.portal} Portal` : ""}</p>
          </div>
          <button className="text-xs text-[#1A2238] hover:underline" onClick={() => nav("/app/fristen")} data-testid="notifications-open-fristen">Kalender</button>
        </div>
        <ul className="max-h-[360px] overflow-auto divide-y divide-[#F3F4F6]">
          {data.items.length === 0 && (
            <li className="px-4 py-8 text-center text-sm text-[#6B7280]" data-testid="notifications-empty">
              <CalendarBlank size={28} weight="duotone" className="mx-auto mb-2 text-[#9CA3AF]" />
              Nichts Dringendes — alles im Griff.
            </li>
          )}
          {data.items.slice(0, 25).map(i => {
            const m = STATUS_META[i.status] || STATUS_META.vorfrist;
            const Icon = i.art === "portal" ? ChatCircleDots : i.art === "frist" ? (i.status === "ueberfaellig" ? Warning : Clock) : BellRinging;
            return (
              <li key={`${i.art}-${i.id}`}>
                <button
                  className="w-full text-left px-4 py-2.5 hover:bg-[#F9FAFB] flex items-start gap-3"
                  onClick={() => nav(i.art === "portal" ? `/app/akten/${i.akte_id}?tab=portal` : i.art === "frist" ? "/app/fristen" : "/app/wiedervorlagen")}
                  data-testid={`notification-item-${i.art}-${i.id}`}
                >
                  <Icon size={18} weight="duotone" className={i.status === "ueberfaellig" ? "text-[#D32F2F] mt-0.5" : "text-[#1A2238] mt-0.5"} />
                  <div className="min-w-0 flex-1">
                    <p className="text-sm text-[#0A192F] truncate">{i.titel}</p>
                    <p className="text-[11px] text-[#6B7280] truncate">
                      {i.art === "portal" ? "Mandantenportal" : i.art === "frist" ? "Frist" : "Wiedervorlage"} · {fmtDate(i.faellig_am)}
                      {i.akte ? ` · ${i.akte.aktenzeichen} ${i.akte.mandant_name || ""}` : ""}
                    </p>
                  </div>
                  <span className={`text-[10px] font-semibold px-1.5 py-0.5 rounded ${m.cls}`}>{m.label}</span>
                </button>
              </li>
            );
          })}
        </ul>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
